import { Box, Card, CardContent, Typography } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo
} from "react";
import { generateSignedUrl } from "../../utils/getPreSignedURL";
import useSanitizeContent from "../../utils/useSanitizeContent";
import useThemeColor from "../../utils/useTheme";
import "./AdminPortal.css";
import "../Extra/RichTextEditor.css"; // Import TipTap CSS for proper rendering
import { useDispatch } from "react-redux";
import { editUser, getCurrentUser } from "../../features/auth/authSlice";
import ReactPlayer from "react-player/lazy"; // Using lazy-loaded version for better performance

const isValidUrl = (url) => {
  return /^(https?:\/\/|www\.)/.test(url);
};

// Function to extract YouTube video ID from various YouTube URL formats
const getYoutubeVideoId = (url) => {
  if (!url) return null;

  // Handle different YouTube URL formats
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  return match && match[2].length === 11 ? match[2] : null;
};

// Enhanced Video component that handles multiple video sources
const VideoPlayer = ({ videoLink, styles }) => {
  const [hasError, setHasError] = useState(false);

  if (!videoLink || hasError) return null;

  return (
    <Card
      sx={{
        margin: "0 auto",
        width: "50%",
        mb: 4,
        boxShadow: 4,
        borderRadius: { xs: 1, sm: 2 },
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          position: "relative",
          paddingTop: "56.25%" /* 16:9 Aspect Ratio */,
          backgroundColor: "#000"
        }}
      >
        <ReactPlayer
          url={videoLink}
          width="100%"
          height="100%"
          onError={() => setHasError(true)}
          controls={true}
          playing={false}
          light={false}
          pip={true}
          stopOnUnmount={true}
          style={{
            position: "absolute",
            top: 0,
            left: 0
          }}
          config={{
            file: {
              attributes: {
                controlsList: "nodownload",
                onContextMenu: (e) => e.preventDefault()
              },
              forceVideo: true
            },
            youtube: {
              playerVars: {
                modestbranding: 1,
                rel: 0
              }
            },
            vimeo: {
              playerOptions: {
                byline: false,
                portrait: false,
                title: false
              }
            }
          }}
        />
      </Box>
    </Card>
  );
};

const AdminPortal = ({
  value: htmlValue,
  userId,
  videoLink,
  checkNotesReminder
}) => {
  const [value, setValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const styles = useThemeColor();
  const dispatch = useDispatch();

  // Track the last processed HTML to prevent unnecessary re-processing
  const lastProcessedHtmlRef = useRef("");
  const isProcessingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (checkNotesReminder) {
      dispatch(editUser({ formData: { checkNotesReminder: true }, userId }));
      dispatch(getCurrentUser());
    }
  }, [userId, dispatch, checkNotesReminder]);

  const updateImageSources = useCallback(async (html, color) => {
    // Prevent re-processing the same content
    if (html === lastProcessedHtmlRef.current && hasInitializedRef.current) {
      return;
    }

    // Prevent concurrent processing
    if (isProcessingRef.current) {
      return;
    }

    isProcessingRef.current = true;
    lastProcessedHtmlRef.current = html;
    setIsLoading(true);

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const images = Array.from(doc.querySelectorAll("img"));

    // Process all images in parallel for better performance
    const imagePromises = images.map(async (img) => {
      const fileName = img.getAttribute("src");

      if (
        fileName &&
        !fileName.startsWith("http") &&
        !fileName.startsWith("data:")
      ) {
        console.log("Generating signed URL for:", fileName);
        try {
          const signedUrl = await generateSignedUrl(
            fileName,
            process.env.REACT_APP_BACKEND_TRAINING_IMAGE_LOCATION
          );
          img.src = signedUrl;
        } catch (error) {
          console.error("Error generating signed URL:", error);
        }
      }

      // Prevent browser from refetching images unnecessarily
      if (!img.hasAttribute("loading")) {
        img.setAttribute("loading", "lazy");
      }
      if (!img.hasAttribute("decoding")) {
        img.setAttribute("decoding", "async");
      }
      // Add referrerpolicy to help with caching
      if (!img.hasAttribute("referrerpolicy")) {
        img.setAttribute("referrerpolicy", "no-referrer");
      }

      // Preserve alignment from style, containerstyle, or data-text-align
      const imgStyle = img.getAttribute("style");
      const containerStyle = img.getAttribute("containerstyle");
      const dataAlign = img.getAttribute("data-text-align");

      img.style.display = "block";

      // Check containerstyle for margin-based alignment (from ImageResize extension)
      if (containerStyle) {
        // Parse margin values to determine alignment
        if (containerStyle.includes("margin: 0px auto 0px 0px") ||
            containerStyle.includes("margin:0px auto 0px 0px")) {
          // Left aligned
          img.style.marginLeft = "0";
          img.style.marginRight = "auto";
        } else if (containerStyle.includes("margin: 0px 0px 0px auto") ||
                   containerStyle.includes("margin:0px 0px 0px auto")) {
          // Right aligned
          img.style.marginLeft = "auto";
          img.style.marginRight = "0";
        } else if (containerStyle.includes("margin: 0px auto") ||
                   containerStyle.includes("margin:0px auto")) {
          // Center aligned
          img.style.marginLeft = "auto";
          img.style.marginRight = "auto";
        }
        // Remove the custom attributes as we've converted them to standard styles
        img.removeAttribute("containerstyle");
        img.removeAttribute("wrapperstyle");
      } else if (imgStyle && imgStyle.includes("text-align")) {
        // Keep existing text-align in style
        const textAlign = imgStyle.match(/text-align:\s*(left|center|right)/)?.[1];
        if (textAlign === "left") {
          img.style.marginLeft = "0";
          img.style.marginRight = "auto";
        } else if (textAlign === "center") {
          img.style.marginLeft = "auto";
          img.style.marginRight = "auto";
        } else if (textAlign === "right") {
          img.style.marginLeft = "auto";
          img.style.marginRight = "0";
        }
      } else if (dataAlign) {
        // Convert data-text-align to margin styles
        if (dataAlign === "left") {
          img.style.marginLeft = "0";
          img.style.marginRight = "auto";
        } else if (dataAlign === "center") {
          img.style.marginLeft = "auto";
          img.style.marginRight = "auto";
        } else if (dataAlign === "right") {
          img.style.marginLeft = "auto";
          img.style.marginRight = "0";
        }
      } else {
        // Default center alignment
        img.style.marginLeft = "auto";
        img.style.marginRight = "auto";
      }

      if (!img.hasAttribute("width")) {
        img.setAttribute("width", "100%");
      }
      if (!img.hasAttribute("height")) {
        img.setAttribute("height", "100%");
      }
    });
    console.log("Processing images:", images.length);
    await Promise.all(imagePromises);

    const links = Array.from(doc.querySelectorAll("a"));
    links.forEach((link) => {
      link.target = "_blank";
      let href = link.getAttribute("href");
      link.style.background = "";
      link.style.color = "";
      if (href) {
        if (href.startsWith("www.")) {
          link.setAttribute("href", `https://${href}`);
        } else if (!isValidUrl(href)) {
          link.setAttribute("href", `https://${href}`);
        }
      }
    });

    const paragraphs = [
      ...Array.from(doc.querySelectorAll("span")),
      ...Array.from(doc.querySelectorAll("strong")),
      ...Array.from(doc.querySelectorAll(["h1", "h2", "h3", "h4", "h5", "h6"]))
    ];
    paragraphs.forEach((p) => {
      p.style.background = "";
      p.style.color = color;
    });

    const allParas = Array.from(doc.querySelectorAll("p"));
    let consecutiveJustifyBrs = [];

    allParas.forEach((p, index) => {
      if (
        p.classList.contains("ql-align-justify") &&
        p.innerHTML.trim() === "<br>"
      ) {
        consecutiveJustifyBrs.push(p);
      } else {
        if (consecutiveJustifyBrs.length > 7) {
          const lastFourElements = consecutiveJustifyBrs.slice(-4);
          lastFourElements.forEach((element) => element.remove());
        }
        consecutiveJustifyBrs = [];
      }
      p.style.color = color;
    });

    if (consecutiveJustifyBrs.length > 7) {
      const lastFourElements = consecutiveJustifyBrs.slice(-4);
      lastFourElements.forEach((element) => element.remove());
    }
    console.log("Final HTML after processing images and links.");
    setValue(doc.body.innerHTML);
    setIsLoading(false);
    hasInitializedRef.current = true;
    isProcessingRef.current = false;
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Reset tracking when htmlValue changes to allow reprocessing
    if (htmlValue !== lastProcessedHtmlRef.current) {
      hasInitializedRef.current = false;
    }

    const initialHtml = htmlValue || "<p>No content available.</p>";

    if (isMounted) {
      console.log("Mounting or updating content in AdminPortal");
      updateImageSources(initialHtml, styles.color);
    }

    return () => {
      isMounted = false;
    };
  }, [htmlValue, updateImageSources]);

  const sanitizedContent = useSanitizeContent(value);

  // Memoize the HTML object to prevent React from recreating DOM nodes
  const htmlContent = useMemo(
    () => ({ __html: sanitizedContent }),
    [sanitizedContent]
  );

  return (
    <Box
      sx={{
        padding: { xs: 0, sm: "0 16px", md: "0 25px" },
        width: "100%",
        margin: "0 auto"
      }}
      display={"flex"}
      gap="16px"
      flexDirection={"column"}
      justifyContent={"center"}
      alignItems={"center"}
    >
      {/* Video Component at the top with full width */}
      {videoLink && <VideoPlayer videoLink={videoLink} styles={styles} />}

      {/* Content Section */}
      {sanitizedContent && sanitizedContent !== "undefined" ? (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: { xs: 1, sm: 2 },
            p: { xs: 1, sm: 2 },
            boxShadow: 2,
            opacity: isLoading ? 0.6 : 1,
            transition: "opacity 0.2s ease-in-out"
          }}
        >
          <div
            className="preview-html tiptap-editor"
            style={{
              marginTop: "10px",
              ".ql-align-center": {
                display: "flex",
                justifyContent: "center"
              },
              p: {
                color: `${styles.color} !important`
              },
              h1: {
                color: `${styles.color} !important`
              }
            }}
            dangerouslySetInnerHTML={htmlContent}
          />
        </Box>
      ) : isLoading ? (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Loading content...
          </Typography>
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
};

export default AdminPortal;
